import shortUUID from "short-uuid";
import { firestore } from "@/services/firestore";
import { addCreationData, addUpdateData } from "@/lib/database";
import { FirestoreDataConverter, WhereFilterOp } from "firebase/firestore";
import { BaseFirestore } from "@/types/v2";

export const generic = {
  get: async <T>(
    id: string,
    path: string,
    docConverter?: FirestoreDataConverter<T>
  ) => {
    return await firestore.getDocument<T>(`${path}/${id}`, docConverter);
  },
  list: async <T>(
    path: string,
    docConverter?: FirestoreDataConverter<T>,
    limitResults: number = 1000,
    orderByField?: string,
    orderDirection: "asc" | "desc" = "asc"
  ) => {
    return firestore.queryDocuments<T>(
      path,
      "_deleted",
      "!=",
      true,
      docConverter,
      limitResults,
      orderByField,
      orderDirection
    );
  },
  create: async <T extends { id: string }>(
    data: Partial<T>,
    path: string,
    docConverter?: FirestoreDataConverter<T>
  ) => {
    if (!data.id) {
      data.id = shortUUID.generate();
      path += data.id;
    }
    const wCreation = addCreationData(data, "client");
    return (await firestore.createDocument<Partial<T>>(
      `${path}`,
      {
        ...wCreation,
      },
      docConverter
    )) as unknown as T;
  },
  // Callers must not rely on the resolved value being the full merged
  // document — on a permission-denied read-back it falls back to the raw
  // write patch instead (see the catch block below).
  update: async <T extends Partial<BaseFirestore>>(
    data: Partial<T>,
    path: string,
    docConverter?: FirestoreDataConverter<T>
  ) => {
    if (data.createdAt) delete data.createdAt;
    if (data.createdBy) delete data.createdBy;
    const wUpdate = addUpdateData(data);
    await firestore.updateDocument<Partial<T>>(
      `${path}`,
      {
        ...wUpdate,
      },
      docConverter
    );
    try {
      return (await firestore.getDocument<T>(`${path}`)) as T;
    } catch (error) {
      // The write above can succeed under a rule that denies the
      // immediately-following read-back — e.g. WEBV3-304's private-tasks
      // rule: a shop member who just made a task private, or an assignee
      // who just cleared their own assignment, can write the document but
      // may no longer match the read rule's creator/assignee disjuncts.
      // Every current caller of generic.update discards this return value
      // anyway (verified across every repository built on top of it), so
      // falling back to the write patch instead of throwing turns what was
      // an unhandled promise rejection into a normal resolved call, without
      // changing behavior for the success path.
      //
      // Checked structurally (`.code`), not via `error instanceof
      // FirebaseError`: Firestore actually throws `FirestoreError`, a
      // subclass of `@firebase/util`'s `FirebaseError`, so `instanceof`
      // would normally hold — but only as long as every consumer resolves
      // the exact same `@firebase/util` module instance. A structural check
      // is immune to that (a bundler/test-runner duplicate-copy resolution
      // would otherwise make the `instanceof` false and silently disable
      // this fallback), and is exactly as precise for a code we don't
      // control the class hierarchy of.
      const code = (error as { code?: unknown } | null)?.code;
      if (code === "permission-denied") {
        return wUpdate as unknown as T;
      }
      throw error;
    }
  },
  delete: async (id: string, path: string, hardDelete?: boolean) => {
    if (hardDelete) {
      return await firestore.deleteDocument(`${path}/${id}`);
    }
    return await firestore.updateDocument(`${path}/${id}`, { _deleted: true });
  },
  subscribeDocument: <T extends { id: string }>(
    id: string,
    path: string,
    callback: (doc: T | undefined) => void
  ) => {
    return firestore.subscribeDocument<T>(`${path}/${id}`, callback);
  },
  subscribeCollection: <T extends { id: string }>(
    path: string,
    callback: (docs: T[]) => void,
    searchParam?: string,
    opsString?: WhereFilterOp,
    equality?: unknown
  ) => {
    return firestore.subscribeCollectionWQuery<T>(
      path,
      callback,
      searchParam,
      opsString,
      equality
    );
  },
};
